"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DATABASE_SHARD_DIRECTORY_PART_HARD_LIMIT_BYTES = exports.DATABASE_SHARD_DIRECTORY_PART_TARGET_BYTES = void 0;
exports.isOpenDatabaseShardDirectoryPart = isOpenDatabaseShardDirectoryPart;
exports.databaseShardDirectoryEntryBytes = databaseShardDirectoryEntryBytes;
exports.databaseShardDirectoryPartId = databaseShardDirectoryPartId;
exports.databaseShardDirectoryPartDocId = databaseShardDirectoryPartDocId;
exports.databaseShardDirectoryPartDocIds = databaseShardDirectoryPartDocIds;
exports.databaseDirectoryDelta = databaseDirectoryDelta;
exports.summarizeDatabaseShardDirectoryPart = summarizeDatabaseShardDirectoryPart;
exports.assertDatabaseShardDirectoryStorageVersion = assertDatabaseShardDirectoryStorageVersion;
exports.assertDatabaseShardDirectoryHead = assertDatabaseShardDirectoryHead;
exports.normalizeDatabaseShardDirectoryHead = normalizeDatabaseShardDirectoryHead;
exports.readStoredDatabaseShardDirectoryHead = readStoredDatabaseShardDirectoryHead;
exports.resolveDatabaseShardDirectoryHead = resolveDatabaseShardDirectoryHead;
exports.databaseShardDirectoryStoredHead = databaseShardDirectoryStoredHead;
exports.isDatabaseShardDirectoryHead = isDatabaseShardDirectoryHead;
exports.databaseShardDirectoryPartShards = databaseShardDirectoryPartShards;
exports.databaseShardDirectoryPartJson = databaseShardDirectoryPartJson;
exports.readStoredDatabaseShardDirectoryPart = readStoredDatabaseShardDirectoryPart;
exports.normalizeDatabaseShardDirectoryPart = normalizeDatabaseShardDirectoryPart;
exports.readDatabaseShardDirectoryParts = readDatabaseShardDirectoryParts;
exports.readDatabaseShardDirectoryHeadLoaded = readDatabaseShardDirectoryHeadLoaded;
exports.databaseShardDirectoryShardRowStart = databaseShardDirectoryShardRowStart;
exports.assembleDatabaseShardDirectory = assembleDatabaseShardDirectory;
exports.selectDatabaseShardDirectoryWindowParts = selectDatabaseShardDirectoryWindowParts;
exports.readDatabaseShardDirectoryLoadedWindow = readDatabaseShardDirectoryLoadedWindow;
exports.databaseShardDirectoryRowCount = databaseShardDirectoryRowCount;
exports.splitDatabaseShardDirectory = splitDatabaseShardDirectory;
exports.databaseShardDirectoryPartMap = databaseShardDirectoryPartMap;
exports.databaseShardDirectoryHeadToYMap = databaseShardDirectoryHeadToYMap;
exports.writeDatabaseShardDirectoryHead = writeDatabaseShardDirectoryHead;
exports.resetDatabaseShardDirectoryPart = resetDatabaseShardDirectoryPart;
exports.writeNewDatabaseShardDirectoryPart = writeNewDatabaseShardDirectoryPart;
const yjs_1 = require("yjs");
const database_document_identity_1 = require("./database-document-identity");
const database_large_data_policy_1 = require("./database-large-data-policy");
const database_manifest_fields_1 = require("./database-manifest-fields");
const database_shard_manifest_crdt_1 = require("./database-shard-manifest-crdt");
const database_storage_errors_1 = require("./database-storage-errors");
const database_document_scheme_1 = require("./generated/database-document-scheme");
const index_1 = require("./index");
function isOpenDatabaseShardDirectoryPart(part) {
    return !('shardCount' in part);
}
/**
 * Stored bytes a directory part aims for (see the shared policy). No part
 * grows past the hard limit of the directory.
 */
exports.DATABASE_SHARD_DIRECTORY_PART_TARGET_BYTES = database_large_data_policy_1.DATABASE_LARGE_DATA_POLICY.shard.directoryPartTargetBytes;
/**
 * Stored bytes no directory part exceeds: the document hard limit a row shard
 * obeys as well. The row-shard limits a manifest carries tune its row shards
 * and never size the directory parts.
 */
exports.DATABASE_SHARD_DIRECTORY_PART_HARD_LIMIT_BYTES = database_large_data_policy_1.DATABASE_LARGE_DATA_POLICY.shard.hardLimitBytes;
/** Stored bytes per field of a CRDT entry besides its key and value. */
const ENTRY_FIELD_OVERHEAD_BYTES = 4;
/** Stored bytes per entry besides its fields. */
const ENTRY_OVERHEAD_BYTES = 8;
const utf8 = new TextEncoder();
function storedValueBytes(value) {
    if (typeof value === 'string')
        return utf8.encode(value).length;
    if (typeof value === 'number')
        return 8;
    if (typeof value === 'boolean')
        return 1;
    if (value instanceof Uint8Array)
        return value.length;
    throw new database_storage_errors_1.DatabaseShardDirectoryFormatError(`Database shard entry stores an unsupported ${typeof value} field`);
}
/**
 * Bytes of the entries measured, per entry: a directory write measures every
 * entry of the parts it writes, and the entries it leaves as they are stay
 * the same objects. Contract for every caller: a manifest entry is never
 * changed once made; a changed shard is a new entry.
 */
const entryBytes = new WeakMap();
/** Stored bytes of one shard entry in a directory part. */
function databaseShardDirectoryEntryBytes(entry) {
    let bytes = entryBytes.get(entry);
    if (bytes === undefined) {
        bytes = (0, database_shard_manifest_crdt_1.databaseShardManifestEntryFields)(entry).reduce((sum, [key, value]) => sum +
            utf8.encode(key).length +
            storedValueBytes(value) +
            ENTRY_FIELD_OVERHEAD_BYTES, ENTRY_OVERHEAD_BYTES);
        entryBytes.set(entry, bytes);
    }
    return bytes;
}
const PART_ID_DIGITS = 5;
/** Part ids count up from 00001 like row-shard ids. */
function databaseShardDirectoryPartId(index) {
    (0, database_manifest_fields_1.assertNonNegativeInteger)(index, 'Database directory part index');
    return String(index + 1).padStart(PART_ID_DIGITS, '0');
}
function databaseShardDirectoryPartDocId(head, partId) {
    return (0, database_document_identity_1.createServerDatabaseDirectoryPartDocId)({
        workspaceId: head.workspaceId,
        databaseId: head.databaseId,
        partId,
    });
}
/** Part document ids of `parts` (all parts of the head by default). */
function databaseShardDirectoryPartDocIds(head, parts = head.parts) {
    return parts.map(part => databaseShardDirectoryPartDocId(head, part.partId));
}
function databaseDirectoryDelta(oldParts, newParts, writtenPartDocIds) {
    const written = new Set(writtenPartDocIds);
    const before = new Set(oldParts);
    const after = new Set(newParts);
    return {
        newParts: newParts.filter(docId => written.has(docId) || !before.has(docId)),
        oldParts: oldParts.filter(docId => written.has(docId) || !after.has(docId)),
        unchangedParts: newParts.filter(docId => !written.has(docId) && before.has(docId)),
    };
}
function summarizeDatabaseShardDirectoryPart(partId, shards) {
    return {
        partId,
        firstShardId: shards[0]?.shardId ?? '',
        lastShardId: shards[shards.length - 1]?.shardId ?? '',
        shardCount: shards.length,
        rowCount: shards.reduce((sum, shard) => sum + shard.rowCount, 0),
    };
}
function normalizePartSummary(value) {
    if ((0, database_manifest_fields_1.readManifestField)(value, 'shardCount') === undefined)
        return { partId: (0, database_manifest_fields_1.readStringField)(value, 'partId') };
    return {
        partId: (0, database_manifest_fields_1.readStringField)(value, 'partId'),
        firstShardId: (0, database_manifest_fields_1.readStringField)(value, 'firstShardId'),
        lastShardId: (0, database_manifest_fields_1.readStringField)(value, 'lastShardId'),
        shardCount: (0, database_manifest_fields_1.readNumberField)(value, 'shardCount'),
        rowCount: (0, database_manifest_fields_1.readNumberField)(value, 'rowCount'),
    };
}
/** Throws the named error for any other storage version than the current. */
function assertDatabaseShardDirectoryStorageVersion(value) {
    const storageVersion = (0, database_manifest_fields_1.readManifestField)(value, 'storageVersion');
    if (storageVersion !== index_1.DATABASE_STORAGE_VERSION)
        throw new database_storage_errors_1.UnsupportedDatabaseStorageVersionError('Database shard directory', storageVersion, index_1.DATABASE_STORAGE_VERSION);
}
function assertDatabaseShardDirectoryHead(head) {
    const { parts, ...fields } = head;
    (0, index_1.assertDatabaseShardManifest)({ ...fields, shards: [] });
    const partIds = new Set();
    for (const [index, part] of parts.entries()) {
        (0, database_document_identity_1.assertNonEmptyIdentifier)(part.partId, 'Database directory part id');
        if (partIds.has(part.partId))
            throw new database_storage_errors_1.DatabaseShardDirectoryFormatError(`Duplicate database directory part id ${part.partId}`);
        partIds.add(part.partId);
        if (isOpenDatabaseShardDirectoryPart(part)) {
            if (index !== parts.length - 1)
                throw new database_storage_errors_1.DatabaseShardDirectoryFormatError(`Database directory part ${part.partId} stores no numbers but is not the last part`);
            continue;
        }
        (0, database_manifest_fields_1.assertNonNegativeInteger)(part.rowCount, 'Database directory part row count');
        (0, database_manifest_fields_1.assertNonNegativeInteger)(part.shardCount, 'Database directory part shard count');
        // An emptied part stays as an entry without shards or rows, so its id is
        // never handed out again.
        if (part.shardCount === 0) {
            if (part.firstShardId || part.lastShardId || part.rowCount)
                throw new database_storage_errors_1.DatabaseShardDirectoryFormatError(`Database directory part ${part.partId} holds no shard but describes some`);
            continue;
        }
        (0, database_document_identity_1.assertNonEmptyIdentifier)(part.firstShardId, 'Database row-shard id');
        (0, database_document_identity_1.assertNonEmptyIdentifier)(part.lastShardId, 'Database row-shard id');
    }
}
/**
 * Head of the directory as stored on the database block (plain object or
 * Y.Map), its open last part by id. Undefined when the block stores none. Any other storage version
 * throws {@link UnsupportedDatabaseStorageVersionError}; a head that still
 * stores shard entries throws {@link DatabaseShardDirectoryFormatError}.
 */
function normalizeDatabaseShardDirectoryHead(value) {
    if (!value)
        return undefined;
    assertDatabaseShardDirectoryStorageVersion(value);
    if ((0, database_manifest_fields_1.readManifestField)(value, 'shards') !== undefined)
        throw new database_storage_errors_1.DatabaseShardDirectoryFormatError('Database shard directory head stores shard entries; they belong in its parts');
    const parts = (0, database_manifest_fields_1.readListField)(value, 'parts');
    if (!parts)
        throw new database_storage_errors_1.DatabaseShardDirectoryFormatError('Database shard directory head has no part list');
    const head = {
        databaseId: (0, database_manifest_fields_1.readStringField)(value, 'databaseId'),
        databaseRootDocId: (0, database_manifest_fields_1.readStringField)(value, 'databaseRootDocId'),
        workspaceId: (0, database_manifest_fields_1.readStringField)(value, 'workspaceId'),
        storageVersion: index_1.DATABASE_STORAGE_VERSION,
        targetBytes: (0, database_manifest_fields_1.readNumberField)(value, 'targetBytes'),
        hardLimitBytes: (0, database_manifest_fields_1.readNumberField)(value, 'hardLimitBytes'),
        ...((0, database_manifest_fields_1.readManifestField)(value, 'nextShardIndex') === undefined
            ? {}
            : { nextShardIndex: (0, database_manifest_fields_1.readNumberField)(value, 'nextShardIndex') }),
        parts: firstOfEach(parts.map(normalizePartSummary), part => part.partId),
    };
    try {
        assertDatabaseShardDirectoryHead(head);
    }
    catch (error) {
        // A stored head that breaks a field rule has the wrong shape for its
        // reader, whatever rule it breaks.
        if (error instanceof database_storage_errors_1.DatabaseShardDirectoryFormatError)
            throw error;
        throw new database_storage_errors_1.DatabaseShardDirectoryFormatError(`Database shard directory head is invalid: ${error instanceof Error ? error.message : String(error)}`);
    }
    return head;
}
/**
 * The stored head as written, with every part summary in stored order: a
 * part id concurrent writers both inserted stays twice. Only writers diff
 * against it, so their change removes the second one.
 */
function readStoredDatabaseShardDirectoryHead(value) {
    const head = normalizeDatabaseShardDirectoryHead(value);
    return (head && {
        ...head,
        parts: (0, database_manifest_fields_1.readListField)(value, 'parts').map(normalizePartSummary),
    });
}
/** `items` without a key that an earlier item has already. */
function firstOfEach(items, key) {
    const seen = new Set();
    return items.filter(item => {
        const id = key(item);
        if (seen.has(id))
            return false;
        seen.add(id);
        return true;
    });
}
/**
 * `stored` with the numbers of its open part read from that part's document,
 * or the document still to load. The open part's highest shard index counts
 * towards `nextShardIndex`, which the head moves on only when a part closes.
 */
function resolveDatabaseShardDirectoryHead(stored, source) {
    const open = stored.parts.at(-1);
    if (!open || !isOpenDatabaseShardDirectoryPart(open))
        return {
            status: 'ready',
            head: stored,
        };
    const read = readDatabaseShardDirectoryParts(stored, source, [open]);
    if (read.status === 'loading')
        return read;
    return {
        status: 'ready',
        head: {
            ...stored,
            ...(stored.nextShardIndex === undefined
                ? {}
                : {
                    nextShardIndex: Math.max(stored.nextShardIndex, (0, index_1.nextDatabaseRowShardIndex)(read.shards)),
                }),
            parts: [
                ...stored.parts.slice(0, -1),
                summarizeDatabaseShardDirectoryPart(open.partId, read.shards),
            ],
        },
    };
}
/**
 * Stored form of `head`: its last part open, by id only. Every writer stores
 * this form, so appending into the last part never changes the head.
 */
function databaseShardDirectoryStoredHead(head) {
    const last = head.parts.at(-1);
    return last
        ? { ...head, parts: [...head.parts.slice(0, -1), { partId: last.partId }] }
        : head;
}
/** Whether a stored directory value is a head (storage version 2 form). */
function isDatabaseShardDirectoryHead(value) {
    return (0, database_manifest_fields_1.readManifestField)(value, 'parts') !== undefined;
}
/**
 * The array of a directory part document holding its shard entries, for the
 * part map {@link databaseShardDirectoryPartMap} handed out for it.
 */
function databaseShardDirectoryPartShards(map) {
    const doc = partDocOf(map);
    if (!doc)
        throw new database_storage_errors_1.DatabaseShardDirectoryFormatError('Database directory part map was not read from its part document');
    return doc.getArray(database_document_scheme_1.DATABASE_DIRECTORY_PART_SHARDS_ARRAY_KEY);
}
function partDocOf(value) {
    return value && typeof value === 'object'
        ? partDocuments.get(value)
        : undefined;
}
/**
 * JSON form of the part a directory part document holds, as readers of
 * document bytes hand it to the readers here: its fields and its entries.
 */
function databaseShardDirectoryPartJson(doc) {
    return {
        ...doc.getMap(database_document_scheme_1.DATABASE_DIRECTORY_PART_MAP_KEY).toJSON(),
        shards: doc.getArray(database_document_scheme_1.DATABASE_DIRECTORY_PART_SHARDS_ARRAY_KEY).toJSON(),
    };
}
/**
 * Parts read from their JSON form, by that value: a writer hands one part to
 * several readers in a write (its shards, the open part's numbers, the
 * stored form), and its entries are normalized once. Readers never change a
 * JSON value they read or the part read from it; a part map is read anew.
 */
const readJsonParts = new WeakMap();
/**
 * Entries of JSON parts as read, per entry object: a part read again keeps
 * the objects of the entries no write changed. Contract for every caller: the
 * JSON form of a part and its entries is never changed once read.
 */
const readJsonEntries = new WeakMap();
function readJsonEntry(value) {
    if (!value || typeof value !== 'object')
        return (0, index_1.normalizeDatabaseShardManifestEntry)(value);
    let entry = readJsonEntries.get(value);
    if (!entry) {
        entry = (0, index_1.normalizeDatabaseShardManifestEntry)(value);
        readJsonEntries.set(value, entry);
    }
    return entry;
}
/**
 * A stored directory part as written (part map or its JSON form), with every
 * entry in stored order: a shard concurrent writers both stored stays twice.
 * Undefined while its document is not loaded, which leaves the map empty.
 */
function readStoredDatabaseShardDirectoryPart(value) {
    const partId = (0, database_manifest_fields_1.readOptionalStringField)(value, 'partId');
    if (!partId)
        return undefined;
    const doc = partDocOf(value);
    const known = doc ? undefined : readJsonParts.get(value);
    if (known)
        return known;
    const shards = doc
        ? databaseShardDirectoryPartShards(value).toArray()
        : (0, database_manifest_fields_1.readListField)(value, 'shards');
    if (!shards)
        throw new database_storage_errors_1.DatabaseShardDirectoryFormatError(`Database directory part ${partId} has no shard list`);
    const part = {
        databaseId: (0, database_manifest_fields_1.readStringField)(value, 'databaseId'),
        partId,
        shards: doc
            ? shards.map(index_1.normalizeDatabaseShardManifestEntry)
            : shards.map(readJsonEntry),
    };
    if (!doc)
        readJsonParts.set(value, part);
    return part;
}
/**
 * A stored directory part (part map or its JSON form), a shard it holds
 * twice counted once. Undefined while its document is not loaded.
 */
function normalizeDatabaseShardDirectoryPart(value) {
    const part = readStoredDatabaseShardDirectoryPart(value);
    return (part && {
        ...part,
        shards: firstOfEach(part.shards, shard => shard.shardId),
    });
}
/** The shards of `parts`, or the part documents still to load. */
function readDatabaseShardDirectoryParts(head, source, parts = head.parts, 
/** Shards of earlier parts: a later copy of one of them counts not again. */
seen = new Set()) {
    const shards = [];
    const partDocIds = [];
    for (const summary of parts) {
        const partDocId = databaseShardDirectoryPartDocId(head, summary.partId);
        const part = source
            ? normalizeDatabaseShardDirectoryPart(source(partDocId))
            : undefined;
        if (part && part.databaseId !== head.databaseId)
            throw new database_storage_errors_1.DatabaseShardDirectoryFormatError(`Database directory part ${summary.partId} belongs to another database`);
        if (part && part.partId !== summary.partId)
            throw new database_storage_errors_1.DatabaseShardDirectoryFormatError(`Database directory part document of ${summary.partId} holds part ${part.partId}`);
        // The part document is the truth about its shards; the head's summary of
        // it is an index that concurrent writers may leave behind, as the row
        // counts of a complete list. Only a part document not written yet is
        // waited for; an emptied part reads as empty.
        if (!part) {
            partDocIds.push(partDocId);
            continue;
        }
        for (const shard of part.shards) {
            if (seen.has(shard.shardId))
                continue;
            seen.add(shard.shardId);
            shards.push(shard);
        }
    }
    return partDocIds.length
        ? { status: 'loading', partDocIds }
        : { status: 'ready', shards };
}
/**
 * The directory as far as its parts are loaded from the first part on, and
 * the shards of every loaded part. A row window inside the leading loaded
 * parts is served before the other parts arrive; a reader of the complete
 * list waits for `missingParts` to empty.
 */
function readDatabaseShardDirectoryHeadLoaded(head, source) {
    const shards = [];
    const loadedShards = [];
    const loadedParts = [];
    const missingParts = [];
    let rowStart = 0;
    const seen = new Set();
    for (const part of head.parts) {
        const read = readDatabaseShardDirectoryParts(head, source, [part], seen);
        if (read.status === 'ready') {
            if (!missingParts.length)
                shards.push(...read.shards);
            loadedShards.push(...read.shards);
            loadedParts.push({
                partDocId: databaseShardDirectoryPartDocId(head, part.partId),
                rowStart,
                shards: read.shards,
            });
        }
        else {
            missingParts.push({ partDocId: read.partDocIds[0], rowStart });
        }
        rowStart += isOpenDatabaseShardDirectoryPart(part)
            ? read.status === 'ready'
                ? read.shards.reduce((sum, shard) => sum + shard.rowCount, 0)
                : 0
            : part.rowCount;
    }
    const resolved = resolveDatabaseShardDirectoryHead(head, source);
    const { parts: _parts, ...fields } = resolved.status === 'ready' ? resolved.head : head;
    const manifest = { ...fields, shards };
    (0, index_1.assertDatabaseShardManifest)(manifest);
    const loadedManifest = missingParts.length
        ? { ...fields, shards: loadedShards }
        : manifest;
    (0, index_1.assertDatabaseShardManifest)(loadedManifest);
    return {
        manifest,
        loadedManifest,
        loadedParts,
        head: resolved.status === 'ready' ? resolved.head : undefined,
        totalRowCount: resolved.status === 'ready' ? rowStart : undefined,
        missingParts,
    };
}
/**
 * The database row at which the first shard `matches` starts, if the
 * directory part holding it is loaded.
 */
function databaseShardDirectoryShardRowStart(loaded, matches) {
    for (const part of loaded.loadedParts) {
        let rowStart = part.rowStart;
        for (const shard of part.shards) {
            if (matches(shard))
                return rowStart;
            rowStart += shard.rowCount;
        }
    }
    return undefined;
}
/** The complete shard list of the head once all of its parts are loaded. */
function assembleDatabaseShardDirectory(head, source) {
    const read = readDatabaseShardDirectoryParts(head, source);
    if (read.status !== 'ready')
        return read;
    const resolved = resolveDatabaseShardDirectoryHead(head, source);
    if (resolved.status !== 'ready')
        return resolved;
    const { parts: _parts, ...fields } = resolved.head;
    const manifest = { ...fields, shards: read.shards };
    (0, index_1.assertDatabaseShardManifest)(manifest);
    return { status: 'ready', manifest };
}
/**
 * Parts holding rows of `[offset, offset + limit)` by the row counts of the
 * head, and the rows before the first of them. Parts without rows are never
 * loaded for a window.
 */
function selectDatabaseShardDirectoryWindowParts(head, request) {
    (0, database_manifest_fields_1.assertNonNegativeInteger)(request.offset, 'Database shard window offset');
    (0, database_manifest_fields_1.assertNonNegativeInteger)(request.limit, 'Database shard window limit');
    if (!request.limit)
        return { parts: [], rowOffset: request.offset };
    const end = request.offset + request.limit;
    const parts = [];
    let rowOffset = 0;
    let partStart = 0;
    for (const part of head.parts) {
        if (partStart >= end)
            break;
        const partEnd = partStart + part.rowCount;
        if (part.rowCount && partEnd > request.offset) {
            if (!parts.length)
                rowOffset = partStart;
            parts.push(part);
        }
        partStart = partEnd;
    }
    return { parts, rowOffset };
}
/**
 * Shards of the parts that hold a row window, taken from `loaded`, a
 * directory read: no part is read again. Until the open part is loaded the
 * row count is unknown: a window before it reads the closed parts alone and
 * counts their rows; a window reaching it waits for it.
 */
function readDatabaseShardDirectoryLoadedWindow(loaded, request) {
    const { head, totalRowCount } = loaded;
    if (!head || totalRowCount === undefined)
        return readClosedPartsWindow(loaded, request);
    const { parts, rowOffset } = selectDatabaseShardDirectoryWindowParts(head, request);
    const missing = new Set(loaded.missingParts.map(part => part.partDocId));
    const window = new Set(parts.map(part => part.partId));
    const partDocIds = [];
    const shards = [];
    // The loaded parts are the parts of the head not missing, in head order.
    let next = 0;
    let rowStart = 0;
    for (const summary of head.parts) {
        const partDocId = databaseShardDirectoryPartDocId(head, summary.partId);
        if (missing.has(partDocId)) {
            if (window.has(summary.partId))
                partDocIds.push(partDocId);
        }
        else {
            const part = loaded.loadedParts[next++];
            if (part?.rowStart !== rowStart)
                throw new Error(`Database directory read of ${head.databaseId} does not match its head`);
            if (window.has(summary.partId))
                shards.push(...part.shards);
        }
        rowStart += summary.rowCount;
    }
    if (partDocIds.length)
        return { status: 'loading', partDocIds };
    return {
        status: 'ready',
        shards,
        rowOffset,
        totalCount: totalRowCount,
    };
}
/**
 * A row window while the open last part (the last missing part) is on its
 * way. It takes the shards of the parts it overlaps, or waits for those of
 * them still missing; the open part has no known end, so a window reaching it
 * waits for it. Meanwhile it counts the rows of the closed parts.
 */
function readClosedPartsWindow(loaded, request) {
    (0, database_manifest_fields_1.assertNonNegativeInteger)(request.offset, 'Database shard window offset');
    (0, database_manifest_fields_1.assertNonNegativeInteger)(request.limit, 'Database shard window limit');
    const end = request.offset + request.limit;
    const open = loaded.missingParts.at(-1);
    // Every part in directory order; each closed part ends where the next starts.
    const parts = [
        ...loaded.loadedParts.map(part => ({ rowStart: part.rowStart, part })),
        ...loaded.missingParts.map(part => ({
            rowStart: part.rowStart,
            missing: part.partDocId,
        })),
    ].sort((a, b) => a.rowStart - b.rowStart);
    const partDocIds = [];
    const shards = [];
    let rowOffset;
    parts.forEach((entry, index) => {
        const isOpen = 'missing' in entry && entry.missing === open.partDocId;
        const partEnd = isOpen
            ? Number.POSITIVE_INFINITY
            : (parts[index + 1]?.rowStart ?? open.rowStart);
        if (!request.limit ||
            partEnd === entry.rowStart ||
            entry.rowStart >= end ||
            partEnd <= request.offset)
            return;
        rowOffset ??= entry.rowStart;
        if ('missing' in entry)
            partDocIds.push(entry.missing);
        else
            shards.push(...entry.part.shards);
    });
    if (partDocIds.length)
        return { status: 'loading', partDocIds };
    return {
        status: 'ready',
        shards,
        rowOffset: rowOffset ?? request.offset,
        totalCount: open.rowStart,
    };
}
function databaseShardDirectoryRowCount(head) {
    return head.parts.reduce((sum, part) => sum + part.rowCount, 0);
}
/**
 * Stored form of `manifest`: its head and the parts in directory order. A part
 * takes entries up to `targetBytes`; an entry that does not fit starts the
 * next part. No part exceeds the directory part hard limit.
 */
function splitDatabaseShardDirectory(manifest, targetBytes = exports.DATABASE_SHARD_DIRECTORY_PART_TARGET_BYTES) {
    (0, index_1.assertDatabaseShardManifest)(manifest);
    (0, database_manifest_fields_1.assertNonNegativeInteger)(targetBytes, 'Database directory part target');
    if (targetBytes === 0 ||
        targetBytes > exports.DATABASE_SHARD_DIRECTORY_PART_HARD_LIMIT_BYTES)
        throw new RangeError('Database directory part target must be positive and within the hard limit');
    const { shards, ...fields } = manifest;
    const sections = [];
    let section = [];
    let sectionBytes = 0;
    for (const shard of shards) {
        const bytes = databaseShardDirectoryEntryBytes(shard);
        if (bytes > exports.DATABASE_SHARD_DIRECTORY_PART_HARD_LIMIT_BYTES)
            throw new database_storage_errors_1.DatabaseShardDirectoryFormatError(`Database shard ${shard.shardId} entry exceeds the directory part hard limit`);
        if (section.length && sectionBytes + bytes > targetBytes) {
            sections.push(section);
            section = [];
            sectionBytes = 0;
        }
        section.push(shard);
        sectionBytes += bytes;
    }
    if (section.length)
        sections.push(section);
    const parts = sections.map((entries, index) => ({
        databaseId: manifest.databaseId,
        partId: databaseShardDirectoryPartId(index),
        shards: entries,
    }));
    const head = {
        ...fields,
        parts: parts.map(part => summarizeDatabaseShardDirectoryPart(part.partId, part.shards)),
    };
    assertDatabaseShardDirectoryHead(head);
    return { head, parts };
}
/** Part maps handed out by {@link databaseShardDirectoryPartMap}, by map. */
const partDocuments = new WeakMap();
/**
 * The `directoryPart` map of a part document. Readers and writers given this
 * map reach the document's entries array through it.
 */
function databaseShardDirectoryPartMap(doc) {
    const map = doc.getMap(database_document_scheme_1.DATABASE_DIRECTORY_PART_MAP_KEY);
    if (map && typeof map === 'object')
        partDocuments.set(map, doc);
    return map;
}
/** Fresh CRDT form of a head for the database block, its last part open. */
function databaseShardDirectoryHeadToYMap(head) {
    assertDatabaseShardDirectoryHead(head);
    const { parts, ...fields } = databaseShardDirectoryStoredHead(head);
    const map = new yjs_1.Map();
    for (const [key, value] of Object.entries(fields))
        map.set(key, value);
    map.set('parts', yjs_1.Array.from(parts.map(part => new yjs_1.Map(Object.entries(part)))));
    return map;
}
/**
 * Stores `head` as the directory of a database block map, replacing what the
 * block stored. The parts it names live in their own documents; callers write
 * them in the same change. Callers run it in their own Yjs transaction.
 */
function writeDatabaseShardDirectoryHead(block, head) {
    block.set(database_shard_manifest_crdt_1.DATABASE_SHARD_MANIFEST_PROP, databaseShardDirectoryHeadToYMap(head));
}
/**
 * Takes a stored part map over as the empty part `part`: the directory that
 * wrote it was replaced, and its entries go with it. Callers run it in their
 * own Yjs transaction.
 */
function resetDatabaseShardDirectoryPart(map, part) {
    const shards = databaseShardDirectoryPartShards(map);
    if (shards.length)
        shards.delete(0, shards.length);
    map.set('databaseId', part.databaseId);
    map.set('partId', part.partId);
}
/**
 * Stores `part` as the whole content of an empty part map. Callers run it in
 * their own Yjs transaction.
 */
function writeNewDatabaseShardDirectoryPart(map, part) {
    if (map.has('partId'))
        throw new database_storage_errors_1.DatabaseShardDirectoryFormatError(`Database directory part ${part.partId} is already stored`);
    summarizeDatabaseShardDirectoryPart(part.partId, part.shards);
    const shards = databaseShardDirectoryPartShards(map);
    map.set('databaseId', part.databaseId);
    map.set('partId', part.partId);
    shards.insert(shards.length, part.shards.map(database_shard_manifest_crdt_1.databaseShardManifestEntryToYMap));
}
